import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import Decimal from 'decimal.js';

export interface SplitAmount {
  num: string;
  unit: string;
  numInt: number;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// 分割金额  console.log(splitAmount("200 万元"));   // { num: "200", unit: "万" }
export interface SplitAmount {
  num: string; // 原始数字（字符串）
  unit: string; // 单位（万/亿/次等）
  numInt: number; // 转成整数（如果能转）
}

export function splitAmount(str: string): SplitAmount {
  // 去掉空格
  str = (str || '').trim();

  // 匹配数字 + 单位（可选），单位不限（忽略“元”）
  // 例子: "200万", "3次", "100元", "1,000亿"
  const match = str.match(/^([\d,.]+)\s*([^\d元]?)元?$/);

  if (match) {
    return {
      num: match[1].replace(/,/g, ''), // 去掉千分位逗号
      unit: match[2] || '', // 单位（万、亿、次等）
      numInt: parseInt(match[1].replace(/,/g, ''), 10)
    };
  }

  // 默认返回
  return { num: str, unit: '', numInt: 0 };
}

export function extractNumber(str: string): string | null {
  const match = (str || '').match(/(\d+(?:\.\d+)?)/);
  return match ? match[1] : null;
}

// 乘以100
export function times100(num: number | string, times: number = 100) {
  return new Decimal(num).times(times);
}

export function isCanParseJson(str: string) {
  try {
    JSON.parse(str);
    return true;
  } catch (error) {
    return false;
  }
}

export function normalizeToJSON(str: string) {
  // 1. 去掉可能的换行/多余空格
  let s = str.trim();

  // 2. 把单引号值改为双引号：{a:'b'} -> {a:"b"}
  s = s.replace(/'([^']*)'/g, (_, inner) => `"${inner}"`);

  // 3. 给未加引号的键名加双引号
  s = s.replace(/([{,])\s*([^"\s][^:]*?)\s*:/g, '$1"$2":');

  // 4. 移除最后可能出现的尾逗号（对象或数组里的）
  s = s.replace(/,\s*(?=[}\]])/g, '');

  return s;
}

/**
 * 计算两个数的插值
 * @param start 起始值（可以是 string 或 number）
 * @param end 结束值（可以是 string 或 number）
 * @param ratio 插值比例，范围 0-1，默认 0.5（中点）
 *              0 表示返回 start，1 表示返回 end
 * @returns 插值结果（Decimal 对象，可通过 .toNumber() 转为 number）
 *
 * @example
 * // 都是正数
 * interpolate(10, 20, 0.5) // 15
 * interpolate("10", "20", 0.5) // 15
 *
 * // 都是负数
 * interpolate(-10, -20, 0.5) // -15
 * interpolate("-10", "-20", 0.5) // -15
 *
 * // 一个正数一个负数
 * interpolate(-10, 10, 0.5) // 0
 * interpolate("-10", "10", 0.5) // 0
 *
 * // 不同比例
 * interpolate(0, 100, 0.25) // 25
 * interpolate(0, 100, 0.75) // 75
 */
export function interpolate(
  start: number | string,
  end: number | string,
  ratio: number = 0.5
): Decimal {
  // 使用 Decimal.js 确保精度
  const startDecimal = new Decimal(start);
  const endDecimal = new Decimal(end);
  const ratioDecimal = new Decimal(ratio);

  // 插值公式: result = start + (end - start) * ratio
  const diff = endDecimal.minus(startDecimal);
  const result = startDecimal.plus(diff.times(ratioDecimal));

  return result;
}
